import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { userReportAPI, userHideAPI } from '../../services/api';
import '../Jobs/ReportJobModal.css';

const ReportUserModal = ({ isOpen, onClose, userId, userName, onSuccess }) => {
  const { isAuthenticated } = useAuth();
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [description, setDescription] = useState('');
  const [hideUser, setHideUser] = useState(true); // Default to checked (hide user)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [complaintId, setComplaintId] = useState(null);

  // Reset form when userId changes or modal opens for a different user
  useEffect(() => {
    if (isOpen) {
      // Reset all form state when modal opens for a new user
      setSelectedReasons([]);
      setDescription('');
      setHideUser(true);
      setError(null);
      setSuccess(false);
      setComplaintId(null);
      setLoading(false);
    }
  }, [userId, isOpen]);

  const reportReasons = {
    spam: 'Spam or Repetitive Content',
    misleading: 'Misleading Information',
    inappropriate: 'Inappropriate Content',
    scam: 'Scam or Fraudulent Activity',
    fake_profile: 'Fake Profile',
    harassment: 'Harassment or Bullying',
    other: 'Other',
  };

  const handleReasonToggle = (reason) => {
    if (selectedReasons.includes(reason)) {
      setSelectedReasons(selectedReasons.filter(r => r !== reason));
      // Clear description if "other" is deselected
      if (reason === 'other') {
        setDescription('');
      }
    } else {
      setSelectedReasons([...selectedReasons, reason]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedReasons.length === 0) {
      setError('Please select at least one reason for reporting this user.');
      return;
    }

    // If "other" is selected, require description
    if (selectedReasons.includes('other') && !description.trim()) {
      setError('Please provide additional details when selecting "Other" as a reason.');
      return;
    }

    if (!isAuthenticated) {
      setError('Please log in to report a user.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await userReportAPI.report({
        reported_user_id: userId,
        reasons: selectedReasons,
        description: selectedReasons.includes('other') ? description.trim() : null,
      });

      if (response.success) {
        // If hideUser is checked, hide the user (though backend already does this automatically)
        // We'll still call it to ensure consistency and handle the onSuccess callback
        if (hideUser) {
          try {
            await userHideAPI.hide(userId);
          } catch (hideErr) {
            // Log but don't fail the report if hiding fails (backend already hides automatically)
            console.warn('Error hiding user after report:', hideErr);
          }
        }

        setSuccess(true);
        setComplaintId(response.data?.complaint_id);
        
        // Call onSuccess callback if provided (to refresh user list, mark as hidden, etc.)
        if (onSuccess) {
          onSuccess(userId, hideUser);
        }
        
        setTimeout(() => {
          onClose();
          // Reset form
          setSelectedReasons([]);
          setDescription('');
          setHideUser(true); // Reset to default
          setSuccess(false);
          setComplaintId(null);
        }, 3000);
      } else {
        setError(response.message || 'Failed to submit report. Please try again.');
      }
    } catch (err) {
      console.error('Error reporting user:', err);
      setError(err.response?.data?.message || err.message || 'Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedReasons([]);
      setDescription('');
      setHideUser(true); // Reset to default
      setError(null);
      setSuccess(false);
      setComplaintId(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="report-job-modal-overlay" onClick={handleClose}>
      <div className="report-job-modal" onClick={(e) => e.stopPropagation()}>
        <div className="report-job-modal-header">
          <h2>Report User</h2>
          <button 
            className="report-job-modal-close"
            onClick={handleClose}
            disabled={loading}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="report-job-modal-content">
          {!isAuthenticated ? (
            <div className="report-job-modal-error">
              <p>Please log in to report a user.</p>
            </div>
          ) : success ? (
            <div className="report-job-modal-success">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <h3>Report Submitted</h3>
              <p>Thank you for reporting this user. Our team will review it shortly.</p>
              {complaintId && (
                <p style={{ marginTop: '12px', fontSize: '14px', color: '#6b7280' }}>
                  Complaint ID: <strong>{complaintId}</strong>
                </p>
              )}
              <p style={{ marginTop: '8px', fontSize: '12px', color: '#9ca3af' }}>
                You will receive a confirmation email shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="report-job-modal-body">
                <p className="report-job-modal-description">
                  Help us maintain a safe and trustworthy community. Please select one or more reasons for reporting <strong>"{userName}"</strong>:
                </p>

                <div className="report-reason-group">
                  {Object.entries(reportReasons).map(([value, label]) => (
                    <label key={value} className="report-reason-option" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <input
                        type="checkbox"
                        name="reasons"
                        value={value}
                        checked={selectedReasons.includes(value)}
                        onChange={() => handleReasonToggle(value)}
                        disabled={loading}
                        style={{ marginTop: '4px', flexShrink: 0 }}
                      />
                      <span className="report-reason-label">{label}</span>
                    </label>
                  ))}
                </div>

                {selectedReasons.includes('other') && (
                  <div className="report-description-group" style={{ marginTop: '16px' }}>
                    <label htmlFor="description" className="report-description-label">
                      Additional Details <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <textarea
                      id="description"
                      className="report-description-textarea"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Please provide additional information about why you are reporting this user..."
                      rows="4"
                      maxLength="1000"
                      disabled={loading}
                      required
                    />
                    <div className="report-description-counter">
                      {description.length}/1000 characters
                    </div>
                  </div>
                )}

                <div className="report-hide-job-group">
                  <label className="report-hide-job-option">
                    <input
                      type="checkbox"
                      checked={hideUser}
                      onChange={(e) => setHideUser(e.target.checked)}
                      disabled={loading}
                    />
                    <span className="report-hide-job-label">
                      Hide this user from my listings
                    </span>
                  </label>
                  <p className="report-hide-job-hint">
                    This user will be hidden from your user listings after reporting.
                  </p>
                </div>

                {error && (
                  <div className="report-job-modal-error-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="report-job-modal-footer">
                <button
                  type="button"
                  className="report-job-modal-cancel"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="report-job-modal-submit"
                  disabled={loading || selectedReasons.length === 0 || (selectedReasons.includes('other') && !description.trim())}
                >
                  {loading ? (
                    <>
                      <div className="spinner-small"></div>
                      Submitting...
                    </>
                  ) : (
                    'Submit Report'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReportUserModal;

